import { useCallback } from "react";
import { AddressSearch, type AddressSuggestion } from "@/shared/components/common/AddressSearch";
import { propertySupportService } from "@/features/properties/model/propertyServices";
import styles from "./PropertiesSearchPage.module.css";

export const PropertiesSearchPage = () => {
  const searchAddresses = useCallback(async (query: string): Promise<AddressSuggestion[]> => {
    if (!query || query.trim().length < 3) {
      return [];
    }

    try {
      const results = await propertySupportService.getRealStateAutoCompletePropertyDetails({
        search: query.trim(),
        search_types: ["A"],
      });

      if (!results || !Array.isArray(results) || results.length === 0) {
        return [];
      }

      return results
        .map((item): AddressSuggestion | null => {
          const addressParts: string[] = [];
          if (item.house) addressParts.push(item.house.trim());
          if (item.street) addressParts.push(item.street.trim());

          const streetAddress = addressParts.join(" ").trim();
          let fullAddress = item.address || "";

          if (!fullAddress && streetAddress) {
            const cityStateZip = [item.city || "", item.state || "", item.zip || ""]
              .filter(Boolean)
              .join(", ");
            fullAddress = cityStateZip ? `${streetAddress}, ${cityStateZip}`.trim() : streetAddress;
          } else if (!fullAddress) {
            fullAddress = [item.city || "", item.state || "", item.zip || ""]
              .filter(Boolean)
              .join(", ")
              .trim();
          }

          if (!fullAddress && !item.id) {
            return null;
          }

          return {
            id: item.id || `address-${Date.now()}-${Math.random()}`,
            address: fullAddress || "Address not available",
            city: item.city,
            state: item.state,
            zipCode: item.zip,
            apn: item.apn,
          };
        })
        .filter((item): item is AddressSuggestion => item !== null);
    } catch (error) {
      console.error("Error searching addresses from properties search:", error);
      return [];
    }
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <p className={styles.welcomeText}>Welcome to Andy..!</p>
        <h1 className={styles.title}>
          Effortlessly Protest Your
          <br />
          <span className={styles.texasText}>Texas</span> Property Taxes
        </h1>

        <div className={styles.searchShell}>
          <AddressSearch
            className={styles.searchWidget}
            placeholder="Enter address"
            buttonText="Get Estimate Savings"
            searchAddresses={searchAddresses}
            onAddressSelect={(address) => {
              console.log("Address selected:", address);
            }}
            onButtonClick={(address) => {
              console.log("Button clicked with address:", address);
            }}
          />
        </div>

        <div className={styles.stepsRow}>
          <div className={styles.stepItem}>
            <span className={styles.stepIcon}>✓</span>
            <span className={styles.stepLabel}>Add Property</span>
          </div>
          <span className={styles.stepDivider} />
          <div className={styles.stepItem}>
            <span className={styles.stepIcon}>✓</span>
            <span className={styles.stepLabel}>Review Tax Details</span>
          </div>
          <span className={styles.stepDivider} />
          <div className={styles.stepItem}>
            <span className={styles.stepIcon}>✓</span>
            <span className={styles.stepLabel}>File Protest</span>
          </div>
        </div>
      </div>
    </div>
  );
};
